import { useGroups, useOptionValues, usePeople, useVehicles } from '@/hooks';
import { Chip, Stack, Typography } from '@mui/material';
import { Fragment } from 'react/jsx-runtime';

const resourceTypeLabels = {
  people: 'People',
  vehicle: 'Vehicle',
  personGroups: 'Groups of People',
  vehicleGroups: 'Groups of Vehicles',
};

export function ApplicableTo({ properties }) {
  return (
    <Fragment>
      <Typography variant="caption" color="textSecondary">
        {resourceTypeLabels[properties.resourceType]}
      </Typography>

      <Stack direction="row" spacing={0.5}>
        {
          {
            people: <People codes={properties.personCodes} />,
            vehicles: (
              <Vehicles
                identificationNumbers={properties.vehicleIdentificationNumbers}
              />
            ),
            personGroups: <Groups codes={properties.personGroupCodes} />,
            vehicleGroups: <Groups codes={properties.vehicleGroupCodes} />,
          }[properties.resourceType]
        }
      </Stack>
    </Fragment>
  );
}

function TypedChip({ type, name }) {
  return (
    <Chip
      label={name}
      icon={type && <Chip color="primary" label={type} size="small" />}
    />
  );
}

function People({ codes }) {
  const {
    query: { data: people },
  } = usePeople();

  if (!codes || codes.length === 0) {
    return null;
  }

  return codes.map((code) => {
    const person = people?.find((p) => p.code === code);
    const name = person
      ? [person.forenames, person.surname].filter(Boolean).join(' ')
      : code;

    return (
      <TypedChip
        key={code}
        type={person?.collarNumber ?? person?.code}
        name={name}
      />
    );
  });
}

function Vehicles({ identificationNumbers }) {
  const {
    query: { data: vehicles },
  } = useVehicles();

  if (!identificationNumbers || identificationNumbers.length === 0) {
    return null;
  }

  return identificationNumbers.map((identificationNumber) => {
    const vehicle = vehicles?.find(
      (v) => v.identificationNumber === identificationNumber,
    );

    return (
      <TypedChip
        key={identificationNumber}
        type={vehicle?.fleetNumber}
        name={vehicle?.registrationNumber ?? identificationNumber}
      />
    );
  });
}

function Groups({ codes }) {
  const {
    query: { data: groups },
  } = useGroups();
  const typeOptions = useOptionValues('groupType');

  if (!codes || codes.length === 0) {
    return null;
  }

  return codes.map((code) => {
    const group = groups?.find((g) => g.code === code);

    return (
      <TypedChip
        key={code}
        type={typeOptions[group?.type]?.label}
        name={group?.name ?? code}
      />
    );
  });
}
